import { GlobalEvents } from '@highlight/ipc'

/** Cadence of the cheap display-change checks in main during a drag. */
const DRAG_PING_INTERVAL_MS = 150
const BLUR_BOUNDS_EMIT_INTERVAL_MS = 32

let lastSentAt = 0
let lastBlurBoundsSentAt = 0

interface SidepanelBlurBounds {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Throttled ping during Mini/sidepanel drags so main can move the overlay
 * window to the display under the cursor (see Overlay's `overlay:drag`
 * handler). Targeted invoke, not `GlobalEvents.emit` — only main listens, and
 * an emit would be re-fanned to every renderer on each ping.
 *
 * Resolves with the display the overlay ended up on, which is how a drop
 * learns which monitor to store its geometry against: the `overlay.displayId`
 * broadcast has not come back by the time the same pointerup turn commits.
 * `null` means the answer is unknown (throttled ping, or main could not
 * resolve a display) and the caller should keep whatever it already had.
 *
 * The throttle lives here so every drag surface shares one cadence; pass
 * `{ force: true }` for the end-of-drag ping so the final position always
 * lands. Mid-drag pings stay fire-and-forget — a lost one just delays the
 * display hop until the next one (≤150ms later).
 */
export function sendOverlayDragPing(options?: { force?: boolean }): Promise<number | null> {
  const now = Date.now()
  if (!options?.force && now - lastSentAt < DRAG_PING_INTERVAL_MS) {
    return Promise.resolve(null)
  }
  lastSentAt = now
  return GlobalEvents.invoke<number | null>('overlay:drag').catch((err) => {
    console.error('[Overlay] drag ping failed:', err)
    return null
  })
}

/** Sends live sidepanel geometry to main so the native blur can follow a drag. */
export function sendSidepanelBlurBounds(bounds: SidepanelBlurBounds): void {
  const now = performance.now()
  if (now - lastBlurBoundsSentAt < BLUR_BOUNDS_EMIT_INTERVAL_MS) {
    return
  }
  lastBlurBoundsSentAt = now
  void GlobalEvents.invoke('overlay:sidepanel-blur-bounds', bounds).catch((err) => {
    console.error('[Overlay] sidepanel blur bounds update failed:', err)
  })
}
